import React, { useState } from "react";
import OwnerAvatar from "@/components/molecules/owner/owner-avatar";
import ContractCard from "@/components/atomics/contract-card";
import ImageUploadButton from "@/components/atomics/image-upload-button";
import EditButton from "@/components/atomics/edit-button";
import InfoIcon from "@/components/atomics/info-icon";
import ShareUrl from "@/components/atomics/share-url";
import CancelButton from "@/components/atomics/cancel-button";
import Input from "@/components/atomics/Input";
import Star from "@/components/atomics/star";
import StarVote from "@/components/atomics/star-vote";
import MotionModal from "@/components/atomics/motion-modal";
import { ViewState, OwnerState } from "@/types/owner-edit";
import { OWNER_EDIT_CONSTANTS } from "@/constants/owner-edit";
import { ownerEditUtils } from "@/utils/owner-edit";
import { NftIndexCounter } from "@/components/atomics/index-counter";
import { formatStarCount } from "@/utils/format";

interface OwnerHeaderProps {
  ownerData: OwnerState;
  viewState: ViewState;
  address: string;
  walletAddress: string | null;
  stars?: number;
  onStarVote?: (starCount: number) => void;
  onUpdateOwnerData: (data: Partial<OwnerState>) => void;
  onUpdateBackground: (file: File, nftNumber: string | null) => void;
  onUpdateAvatar: (file: File, nftNumber: string | null) => void;
  onRemoveAvatar: () => void;
  onShowEdit: () => void;
  onShowBackgroundModal: () => void;
  onShowAvatarModal: () => void;
  onShowReportModal: () => void;
}

const OwnerHeader: React.FC<OwnerHeaderProps> = ({
  ownerData,
  viewState,
  address,
  walletAddress,
  stars = 0,
  onStarVote,
  onUpdateOwnerData,
  onUpdateBackground,
  onUpdateAvatar,
  onRemoveAvatar,
  onShowEdit,
  onShowBackgroundModal,
  onShowAvatarModal,
  onShowReportModal,
}) => {
  const [showStarVote, setShowStarVote] = useState(false);
  const experience = ownerData.extendInfo?.experience?.experience;

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onUpdateOwnerData({ username: e.target.value });
  };

  const handleStarVoteClick = (starCount: number) => {
    if (onStarVote) {
      onStarVote(starCount);
    }
    setShowStarVote(false);
  };

  // Wrapper functions to match ImageUploadButton interface
  const handleBackgroundUpdate = (file: File | null) => {
    if (file) onUpdateBackground(file, null);
  };

  const handleAvatarUpdate = (file: File | null) => {
    if (file) onUpdateAvatar(file, null);
  };
  // May be 0 index
  const avatarIndex = ownerData.extendInfo?.avatar_index_in;
  const backgroundIndex = ownerData.extendInfo?.background_index_in;

  return (
    <div className="relative w-full custom-aspect-430-180">
      {backgroundIndex !== null && backgroundIndex !== undefined && (
        <NftIndexCounter index={backgroundIndex} />
      )}
      <img
        src={ownerData.background}
        className={`${viewState.isEditView ? "opacity-40" : ""}`}
        alt="background"
      />

      {viewState.isEditView && (
        <ImageUploadButton
          className="absolute top-[24px] right-[24px]"
          maxResolution={OWNER_EDIT_CONSTANTS.BACKGROUND_MAX_RESOLUTION}
          maxFileSize={OWNER_EDIT_CONSTANTS.MAX_BACKGROUND_SIZE_MB}
          onFileSelect={handleBackgroundUpdate}
          onClick={onShowBackgroundModal}
        />
      )}

      {viewState.isOwnerView && viewState.showOwnerView && (
        <EditButton
          className={`${
            viewState.isEditView ? "opacity-40" : ""
          } absolute bottom-[-52px] left-[40px] z-[1] hover:cursor-pointer`}
          color={`${viewState.isEditView ? "white" : "#0099FE"}`}
          onClick={onShowEdit}
        />
      )}

      {(!viewState.showOwnerView || !viewState.isOwnerView) &&
        !viewState.isEditView && (
          <div
            className="absolute bottom-[-49px] left-[20px] z-[1] hover:cursor-pointer flex gap-[10px] items-center justify-center"
            onClick={() => setShowStarVote(true)}
          >
            <Star />
            <span className="text-[14px]">{formatStarCount(stars)}</span>
          </div>
        )}

      <InfoIcon
        className={`${
          viewState.isEditView ? "opacity-40" : ""
        } w-[31px] h-[31px] absolute bottom-[-52px] right-[75px] z-[1] hover:cursor-pointer`}
        onClick={onShowReportModal}
      />

      <ShareUrl
        className={`${
          viewState.isEditView ? "opacity-40 pointer-events-none" : ""
        } absolute bottom-[-49px] right-[28px] z-[1] hover:cursor-pointer`}
        buttonColor={`${viewState.isEditView ? "white" : "#0099FE"}`}
        path={`owner/${address}`}
        walletAddress={walletAddress}
      />

      <div className="w-full absolute left-[50%] translate-x-[-50%] translate-y-[-80px] flex flex-col items-center justify-center">
        <div className="relative">
          {avatarIndex !== null && avatarIndex !== undefined && (
            <div className="absolute bottom-[2px] left-[50%] translate-x-[-50%] bg-blue-950 opacity-80 z-10 px-[10px] text-[12px] font-silkscreen text-white">
              #{avatarIndex}
            </div>
          )}
          <OwnerAvatar
            className={`${viewState.isEditView ? "opacity-40" : ""} w-[143px]`}
            image={ownerData.avatar}
            experience={experience || 0}
            viewState={viewState}
          />

          {viewState.isEditView && (
            <>
              <ImageUploadButton
                className="absolute z-[10] top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]"
                maxResolution={OWNER_EDIT_CONSTANTS.AVATAR_MAX_RESOLUTION}
                maxFileSize={OWNER_EDIT_CONSTANTS.MAX_AVATAR_SIZE_MB}
                onFileSelect={handleAvatarUpdate}
                onClick={onShowAvatarModal}
              />
              {!ownerEditUtils.isDefaultAvatar(ownerData.avatar) && (
                <CancelButton
                  className="absolute z-[11] top-[0%] right-[0%] w-[31px] h-[31px]"
                  color="red"
                  onClick={onRemoveAvatar}
                />
              )}
            </>
          )}
        </div>

        <div
          className={`${viewState.isEditView ? "opacity-100" : ""} text-[32px]`}
        >
          {!viewState.isEditView ? (
            <span>{ownerData.username}</span>
          ) : (
            <Input
              className="w-[calc(100vw-60px)] h-[32px] text-[32px] -mt-[15px] mb-[20px]"
              value={ownerData.username}
              onChange={handleUsernameChange}
            />
          )}
        </div>

        <ContractCard
          className={`${
            viewState.isEditView ? "opacity-40 pointer-events-none" : ""
          } pt-[10px] pb-[10px] w-[calc(100vw-61px)] max-w-[374px]`}
          length={12}
          text="WALLET"
          address={address}
        />
      </div>

      <MotionModal isOpen={showStarVote}>
        <StarVote
          onClick={handleStarVoteClick}
          onClose={() => setShowStarVote(false)}
        />
      </MotionModal>
    </div>
  );
};

export default OwnerHeader;
